import React, { useState, useRef, useEffect } from 'react';
import realApiService from '../../utils/realApiService';
import sessionService from '../../utils/sessionService.js';

const MentionTextarea = ({ 
  value, 
  onChange, 
  placeholder = "Add a comment...", 
  cardId,
  className = "",
  rows = 3,
  disabled = false
}) => {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [mentionSearch, setMentionSearch] = useState('');
  const [mentionStartPos, setMentionStartPos] = useState(-1);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  
  const textareaRef = useRef(null);
  const suggestionsRef = useRef(null);

  // Debounce search to avoid too many API calls
  const searchTimeoutRef = useRef(null);

  // Track the latest request ID to prevent race conditions
  const latestRequestId = useRef(0);

  const fetchMentionSuggestions = async (searchTerm) => {
    if (!cardId) return;
    
    // Increment request ID
    const requestId = ++latestRequestId.current;
    
    try {
      setLoading(true);
      let apiData = null;

      // Try API first
      try {
        const response = await realApiService.cards.getMentionAutocomplete(cardId, searchTerm);
        
        // Check if this is still the latest request
        if (requestId !== latestRequestId.current) return;

        if (response.success && Array.isArray(response.data) && response.data.length > 0) {
          apiData = response.data;
        }
      } catch (apiError) {
        console.warn('API fetch failed for mentions, falling back to local filter:', apiError);
      }

      // Check if this is still the latest request before setting state
      if (requestId !== latestRequestId.current) return;

      // Use API data if available, otherwise fallback to local filtering
      if (apiData) {
        setSuggestions(apiData);
      } else {
        // Fallback: load organization team members and filter by search
        try {
          const orgId = sessionService.getOrganizationId?.();
          if (orgId) {
            const { default: teamService } = await import('../../utils/teamService');
            const members = await teamService.getTeamMembers(orgId);
            
            // Check if this is still the latest request
            if (requestId !== latestRequestId.current) return;

            const filtered = (members || [])
              .map(m => ({
                id: m.id || m.user_id,
                name: m.name || `${m.firstName || m.first_name || ''} ${m.lastName || m.last_name || ''}`.trim() || m.email?.split('@')[0] || 'User',
                primary_username: m.username || m.handle || m.email?.split('@')[0] || (m.name || '').toLowerCase().replace(/\s+/g, ''),
                email: m.email,
                avatar: m.avatar || m.profile_picture || null,
              }))
              .filter(m => {
                const s = (searchTerm || '').toLowerCase();
                return !s || m.name.toLowerCase().includes(s) || m.email?.toLowerCase().includes(s) || m.primary_username?.toLowerCase().includes(s);
              })
              .slice(0, 15);
            setSuggestions(filtered);
          } else {
            setSuggestions([]);
          }
        } catch (e) {
          console.warn('Fallback mention search failed:', e);
          if (requestId === latestRequestId.current) {
            setSuggestions([]);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching mention suggestions:', error);
      if (requestId === latestRequestId.current) {
        setSuggestions([]);
      }
    } finally {
      if (requestId === latestRequestId.current) {
        setLoading(false);
      }
    }
  };

  const handleTextChange = (e) => {
    const newValue = e.target.value;
    const cursorPos = e.target.selectionStart;
    
    onChange(newValue);

    // Check for @ mentions
    const textBeforeCursor = newValue.substring(0, cursorPos);
    const mentionMatch = textBeforeCursor.match(/@([a-zA-Z0-9._-]*)$/);
    
    if (mentionMatch) {
      const searchTerm = mentionMatch[1];
      const startPos = cursorPos - mentionMatch[0].length;
      
      setMentionStartPos(startPos);
      setMentionSearch(searchTerm);
      setShowSuggestions(true);
      setSelectedSuggestionIndex(0);
      
      // Debounce the search
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      
      searchTimeoutRef.current = setTimeout(() => {
        fetchMentionSuggestions(searchTerm);
      }, 300);
    } else {
      setShowSuggestions(false);
      setMentionStartPos(-1);
      setMentionSearch('');
      setSuggestions([]);
    }
  };

  const insertMention = (member) => {
    if (mentionStartPos === -1) return;
    
    const beforeMention = value.substring(0, mentionStartPos);
    const afterMention = value.substring(mentionStartPos + mentionSearch.length + 1); // +1 for @
    const mentionText = `@${member.primary_username}`;
    
    const newValue = beforeMention + mentionText + ' ' + afterMention;
    onChange(newValue);
    
    // Close suggestions
    setShowSuggestions(false);
    setMentionStartPos(-1);
    setMentionSearch('');
    setSuggestions([]);
    
    // Focus back to textarea and position cursor
    setTimeout(() => {
      if (textareaRef.current) {
        const newCursorPos = beforeMention.length + mentionText.length + 1;
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(newCursorPos, newCursorPos);
      }
    }, 0);
  };

  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) return;
    
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedSuggestionIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedSuggestionIndex(prev => 
          prev > 0 ? prev - 1 : suggestions.length - 1
        );
        break;
      case 'Enter':
      case 'Tab':
        if (suggestions[selectedSuggestionIndex]) {
          e.preventDefault();
          insertMention(suggestions[selectedSuggestionIndex]);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        break;
    }
  };

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        textareaRef.current && 
        !textareaRef.current.contains(event.target) &&
        suggestionsRef.current && 
        !suggestionsRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div className="relative">
      <textarea
        ref={textareaRef}
        value={value}
        onChange={handleTextChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        rows={rows}
        disabled={disabled}
        className={`w-full px-3 py-2 border border-input bg-background text-foreground rounded-md focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent resize-none ${className}`}
      />
      
      {showSuggestions && (
        <div 
          ref={suggestionsRef}
          className="absolute z-50 w-full mt-1 bg-popover border border-border rounded-md shadow-lg max-h-48 overflow-y-auto"
        >
          {loading ? (
            <div className="px-4 py-2 text-sm text-muted-foreground">
              Loading suggestions...
            </div>
          ) : suggestions.length > 0 ? (
            suggestions.map((member, index) => {
              const fn = member.first_name || member.firstName || '';
              const ln = member.last_name || member.lastName || '';
              const initials = ((fn[0] || '') + (ln[0] || '')).toUpperCase() || (member.name?.slice(0,2) || 'U');
              return (
              <div
                key={member.id}
                onClick={() => insertMention(member)}
                className={`px-4 py-2 cursor-pointer text-sm hover:bg-muted transition-colors ${
                  index === selectedSuggestionIndex ? 'bg-primary/20 border-l-2 border-primary' : ''
                }`}
              >
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center text-xs font-medium text-white">
                    {initials}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-foreground truncate">
                      {member.name}
                    </div>
                    <div className="text-xs text-muted-foreground truncate">
                      @{member.primary_username} • {member.email}
                    </div>
                  </div>
                </div>
              </div>
            )})
          ) : (
            <div className="px-4 py-2 text-sm text-muted-foreground">
              No members found
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MentionTextarea;
